import { DialogHTMLAttributes, forwardRef, useEffect, useRef } from 'react';

export type DialogProps = Omit<
	DialogHTMLAttributes<HTMLDialogElement>,
	'onClose' | 'onCancel' | 'open'
> & {
	open?: boolean;
	onClose?: (event: Event) => void;
	onCancel?: (event: Event) => void;
};

const Dialog_ = (
	{ open, onClose, onCancel, ...props }: DialogProps,
	forwardedRef: React.ForwardedRef<HTMLDialogElement>,
) => {
	const localRef = useRef<HTMLDialogElement>(null);
	const ref =
		forwardedRef != null && 'current' in forwardedRef ? forwardedRef : localRef;

	useEffect(() => {
		const modalElement = ref.current;

		if (modalElement == null) return;
		const abortController = new AbortController();

		// Listen for click on backdrop
		modalElement.addEventListener(
			'click',
			(event: Event) => {
				if (event.target !== event.currentTarget) return;

				onCancel?.(event);
			},
			{ signal: abortController.signal },
		);
		modalElement.addEventListener(
			'close',
			(event: Event) => {
				event.preventDefault();
				onClose?.(event);
			},
			{ signal: abortController.signal },
		);
		modalElement.addEventListener(
			'cancel',
			(event: Event) => {
				event.preventDefault();
				onCancel?.(event);
			},
			{ signal: abortController.signal },
		);

		return () => {
			abortController.abort();
		};
	}, [onClose, onCancel, ref]);
	useEffect(() => {
		if (open && !ref.current?.open) {
			ref.current?.showModal();
			return;
		}
		if (!open && ref.current?.open) {
			ref.current?.close();
			return;
		}
	}, [open, ref]);
	return <dialog {...props} ref={ref} />;
};

export const Dialog = forwardRef(Dialog_);
